export const useAppearance = () => {
  const appearance = useState<'light' | 'dark' | 'system'>('appearance', () => 'dark')

  // Track actual dark state (reactive to DOM)
  const isDark = useState<boolean>('isDark', () => false)

  const updateAppearance = (value: 'light' | 'dark' | 'system') => {
    appearance.value = value

    if (import.meta.client) {
      localStorage.setItem('appearance', value)

      // Determine actual dark state
      let shouldBeDark: boolean
      if (value === 'system') {
        shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      } else {
        shouldBeDark = value === 'dark'
      }

      // Apply to DOM
      if (shouldBeDark) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }

      // Update reactive state
      isDark.value = shouldBeDark
    }
  }

  // IMPORTANT: read the client's real theme AFTER hydration, never during setup.
  // The pre-hydration inline script sets the <html> .dark class, but the server
  // renders with isDark=false. If we read it during setup the client's first
  // render would diverge from the server markup -> Vue hydration mismatch (which
  // patches the DOM mid-load and can break LCP). onMounted runs post-hydration,
  // so server and client render identically, then the real theme is applied.
  onMounted(() => {
    const saved = localStorage.getItem('appearance') as 'light' | 'dark' | 'system' | null
    if (saved) {
      appearance.value = saved
    }

    isDark.value = document.documentElement.classList.contains('dark')

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      if (appearance.value === 'system') {
        document.documentElement.classList.toggle('dark', e.matches)
        isDark.value = e.matches
      }
    })
  })

  return {
    appearance,
    isDark: computed(() => isDark.value),
    updateAppearance
  }
}
